import { flutterMessenger } from './flutterMessenger.js';
import { loaderOverlay, getQueryParams } from './utils.js';
import { templates } from './templates/index.js';
import { setLang } from './localization.js';
import { dataStorage, userInfo } from './userStorage.js';
import { Buffer } from "buffer";

let refreshedJwt = null;
// jwt, отвергнутый бэкендом (401/403), больше не отправляем: показываем «Сессия истекла».
// token_expired хосту — один раз на jwt: обычно хост за <1 с присылает новый jwt через onAppRefresh.
// Метки jwt живут в sessionStorage — переживают перезагрузку страницы хостом (иначе петля
// getprofile → 403 → перезагрузка шла по ~20 запросов в секунду), сбрасываются при перезапуске
// приложения. Другой jwt пробуем как обычно.
const EXPIRED_JWT_KEY = 'expired_jwt';
const EXPIRED_JWT_MAX = 5;
let expiredJwtTags = [];

function jwtTag(jwt) {
  return jwt.slice(-16);
}

function loadExpiredJwtTags() {
  try {
    const saved = JSON.parse(sessionStorage.getItem(EXPIRED_JWT_KEY));
    if (Array.isArray(saved)) return saved;
  } catch (e) {}
  return expiredJwtTags;
}

function isJwtExpired(jwt) {
  return loadExpiredJwtTags().includes(jwtTag(jwt));
}

function markJwtExpired(jwt) {
  const tag = jwtTag(jwt);
  expiredJwtTags = [...loadExpiredJwtTags().filter((t) => t !== tag), tag].slice(-EXPIRED_JWT_MAX);
  try {
    sessionStorage.setItem(EXPIRED_JWT_KEY, JSON.stringify(expiredJwtTags));
  } catch (e) {}
}

// Не чаще одного getprofile в секунду, даже если хост шлёт разные jwt подряд.
const MIN_PROFILE_REQUEST_INTERVAL_MS = 1000;
let lastProfileRequestAt = 0;
// Повторы только для сетевых ошибок, таймаутов и 5xx: 1, 2, 4 с (итого до ~7 с до карточки ошибки).
const RETRY_DELAYS_MS = [1000, 2000, 4000];
const RETRY_DELAY_CAP_MS = 30000;
let loadUserInFlight = null;
let loadUserInFlightJwt = null;
let loadUserRerunRequested = false;
let referralApplied = false;
let referralAppliedAt = 0;
let isPageUnloading = false;
window.addEventListener('beforeunload', () => { isPageUnloading = true; });
window.addEventListener('pagehide', () => { isPageUnloading = true; });

function getCurrentJwt() {
  return refreshedJwt || getQueryParams().jwt;
}

function sendLogToFlutter(message) {
  try {
    const encoded = Buffer.from(message).toString('base64');
    flutterMessenger.send('loki_log', encoded);
  } catch (e) {
    console.error('sendLogToFlutter failed:', e);
  }
}

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

// Если вкладка в фоне — ждём, пока её снова откроют, и только потом повторяем запрос.
function waitForVisible() {
  if (!document.hidden) return Promise.resolve();
  return new Promise((resolve) => {
    const onChange = () => {
      if (!document.hidden) {
        document.removeEventListener('visibilitychange', onChange);
        resolve();
      }
    };
    document.addEventListener('visibilitychange', onChange);
  });
}

// Повторяем только транспортные сбои из fetchJson (нет сети, таймаут, битый JSON, 5xx, 408, 429).
// Доменные ошибки бэкенда (HTTP 200 с error_message) и 401/403 не повторяем.
function isRetryableProfileError(result) {
  if (!result || !result.transport) return false;
  const st = result.status;
  if (st === undefined || st === null) return true;
  return st >= 500 || st === 408 || st === 429;
}

function hasNoGroup(card) {
  const g = card.group;
  return g === null || g === undefined || g === '';
}

function buildBlocks(cards) {
  const blocks = [];
  const emittedGroups = new Set();
  for (let i = 0; i < cards.length; i++) {
    const card = cards[i];
    if (hasNoGroup(card)) {
      blocks.push({ type: 'single', card });
      continue;
    }
    const g = card.group;
    if (emittedGroups.has(g)) continue;
    emittedGroups.add(g);
    const groupCards = cards.filter((c) => !hasNoGroup(c) && c.group === g);
    if (groupCards.length >= 2) {
      blocks.push({ type: 'group', group: g, cards: groupCards });
    } else {
      blocks.push({ type: 'single', card: groupCards[0] });
    }
  }
  return blocks;
}

function renderCard(card, params) {
  if (card.template && templates[card.template]) {
    return templates[card.template]({
      ...card,
      jwt: params.jwt,
      deviceId: params.deviceId,
    });
  }
  return templates.default(card);
}

function renderReferralBanner() {
  return `
    <div class="referral-banner" id="referral-banner">
      <div class="referral-banner-header" id="referral-banner-header">
        <div class="referral-banner-header-left">
          <svg class="referral-banner-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M20 12V22H4V12" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M22 7H2V12H22V7Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M12 22V7" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M12 7H7.5C6.83696 7 6.20107 6.73661 5.73223 6.26777C5.26339 5.79893 5 5.16304 5 4.5C5 3.83696 5.26339 3.20107 5.73223 2.73223C6.20107 2.26339 6.83696 2 7.5 2C11 2 12 7 12 7Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M12 7H16.5C17.163 7 17.7989 6.73661 18.2678 6.26777C18.7366 5.79893 19 5.16304 19 4.5C19 3.83696 18.7366 3.20107 18.2678 2.73223C17.7989 2.26339 17.163 2 16.5 2C13 2 12 7 12 7Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          <!--
          <svg class="referral-banner-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="1.5"/>
            <path d="M12 8V16M8 12H16" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
          </svg>
          -->
          <span class="referral-banner-text">${window.strings.referral_prompt}</span>
        </div>
        <svg class="referral-banner-chevron" width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M6 9L12 15L18 9" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </div>
      <div class="referral-banner-body" id="referral-banner-body">
        <div class="referral-banner-input-row">
          <input
            type="text"
            class="referral-banner-input"
            id="referral-input"
            placeholder="${window.strings.referral_placeholder}"
            autocomplete="off"
            autocorrect="off"
            autocapitalize="off"
            spellcheck="false"
          />
          <button class="referral-banner-submit" id="referral-submit">
            <span class="referral-submit-text">${window.strings.referral_apply}</span>
            <span class="referral-submit-loader" style="display:none;"></span>
          </button>
        </div>
        <div class="referral-banner-error" id="referral-error" style="display:none;"></div>
      </div>
      <div class="referral-banner-success" id="referral-success" style="display:none;">
        <svg class="referral-success-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M20 6L9 17L4 12" stroke="#22c55e" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
        <span class="referral-success-text">${window.strings.referral_applied}</span>
      </div>
    </div>
  `;
}

const app = {
  async run() {
    console.log('Поехали');
    const params = getQueryParams();
    
    // Применяем параметр dark сразу, чтобы перекрыть системную тему
    const { dark } = params;
    const body = document.querySelector('body');
    const html = document.documentElement;
    
    // Убеждаемся, что используется только theme-airlink
    body.classList.add('theme-airlink');
    
    // Применяем параметр dark для theme-airlink
    if (dark === 'true') {
      body.classList.add('dark');
      html.style.backgroundColor = '#222222';
    } else {
      body.classList.remove('dark');
      html.style.backgroundColor = '#ffffff';
    }

    flutterMessenger.registerCallbacks();
    window.onAppRefresh = (jwt) => this.refreshUserData(jwt);
    const container = document.getElementById('user-cards');
    container.addEventListener('click', (event) => {
      if (event.target && event.target.matches('.user-card-button')) {

        let callbackData = dataStorage[event.target.getAttribute('callback-data-key') || null];
        if (callbackData) {
          callbackData = Buffer.from(JSON.stringify(callbackData)).toString("base64");
        }

        flutterMessenger.send('button_click', JSON.stringify({
          link: event.target.getAttribute('click-link'),
          callback: event.target.getAttribute('click-callback'),
          deeplink: event.target.getAttribute('click-deeplink'),
          need_reconnect: Boolean(event.target.getAttribute('click-need-reconnect')),
          callbackData: callbackData,
        }));
      }
    });

    await this.loadUser();
    loaderOverlay.hide();
  },

  // Все вызовы (старт, onAppRefresh, pull-to-refresh) коалесцируются в один запрос.
  // Если во время запроса пришёл другой jwt, после завершения текущего запускаем ещё один с новым.
  loadUser() {
    const jwt = getCurrentJwt();
    if (loadUserInFlight) {
      if (jwt !== loadUserInFlightJwt) loadUserRerunRequested = true;
      return loadUserInFlight;
    }
    loadUserInFlightJwt = jwt;
    loadUserRerunRequested = false;
    loadUserInFlight = this.loadUserOnce().finally(() => {
      loadUserInFlight = null;
      loadUserInFlightJwt = null;
      if (loadUserRerunRequested && !isPageUnloading) {
        loadUserRerunRequested = false;
        this.loadUser().catch((e) => console.error('loadUser rerun failed:', e));
      }
    });
    return loadUserInFlight;
  },

  async loadUserOnce() {
    const jwt = getCurrentJwt();
    const { deviceId } = getQueryParams();
    if (!jwt) {
      alert('JWT is missing');
      return;
    }

    // Декодируем payload JWT для получения client_id
    let clientId = '';
    try {
      const payload = JSON.parse(atob(jwt.split('.')[1]));
      clientId = payload.client_id;
    } catch (e) {
      console.error('Failed to decode JWT payload:', e);
    }

    // Этот jwt уже отвергнут бэкендом — не запрашиваем, сразу просим перезапустить приложение.
    if (isJwtExpired(jwt)) {
      this.renderAuthError(clientId);
      return;
    }

    const url = 'https://airlink-gate.ru/webhook/9aExHcMnHiLpCdxDi6AX67ay/getprofile/v2';
    const requestOptions = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${jwt}`,
      },
      body: JSON.stringify({ device_id: deviceId }),
    };

    let userData = {};
    for (let attempt = 0; ; attempt++) {
      // Первый запрос делаем всегда; повторы — только когда вкладка на экране.
      if (attempt > 0) await waitForVisible();
      const sinceLast = Date.now() - lastProfileRequestAt;
      if (sinceLast < MIN_PROFILE_REQUEST_INTERVAL_MS) {
        await sleep(MIN_PROFILE_REQUEST_INTERVAL_MS - sinceLast);
      }
      lastProfileRequestAt = Date.now();
      userData = await this.fetchJson(url, requestOptions);
      if (isPageUnloading) return;
      if (!isRetryableProfileError(userData) || attempt >= RETRY_DELAYS_MS.length) break;
      const delay = Math.min(RETRY_DELAYS_MS[attempt], RETRY_DELAY_CAP_MS);
      console.warn(`getprofile failed (${userData.error_message}), retry ${attempt + 1}/${RETRY_DELAYS_MS.length} in ${delay}ms`);
      await sleep(delay);
    }

    if (userData.status === 401 || userData.status === 403) {
      markJwtExpired(jwt);
      flutterMessenger.send('token_expired');
      this.renderAuthError(clientId);
      return;
    }

    if (userData.lang) {
      setLang(userData.lang);
    }
    
    const container = document.getElementById('user-cards');
    if (userData.error_message) {
      container.innerHTML = templates.key({ clientId });
      container.insertAdjacentHTML('beforeend', templates.default({
        icon: 'danger',
        title: { text: window.strings.connection_error },
        content: { text: userData.error_message },
        button: { title: window.strings.support, link: 'https://vk.com/id1107852807', style: 'on' },
      }));
      return;
    }
    userInfo.setData(userData);

    try {
      this.fillUserData(userData, clientId);
    } catch (e) {
      sendLogToFlutter(`fillUserData error: ${e.message}\n${e.stack || ''}`);
      throw e;
    }
  },

  renderAuthError(clientId) {
    const container = document.getElementById('user-cards');
    container.innerHTML = templates.key({ clientId });
    container.insertAdjacentHTML('beforeend', templates.default({
      icon: 'danger',
      title: { text: window.strings.auth_error },
      content: { text: window.strings.auth_error_message },
      button: { title: window.strings.support, link: 'https://vk.com/id1107852807', style: 'on' },
    }));
  },

  async fetchJson(url, options = {}, timeoutMs = 15000) {
    const controller = new AbortController();
    const timerId = setTimeout(() => controller.abort(), timeoutMs);
    try {
      const response = await fetch(url, { ...options, signal: controller.signal });
      clearTimeout(timerId);
      if (!response.ok) {
        const responseBody = await response.text().catch(() => '');
        if (response.status >= 404) {
          sendLogToFlutter(
            `HTTP Error ${response.status}\n` +
            `Dashboard URL: ${window.location.href}\n` +
            `Request: ${options.method || 'GET'} ${url}\n` +
            `Request Body: ${options.body || ''}\n` +
            `Response: ${responseBody}`
          );
        }
        return { error_message: `Нет сети (HTTP error: ${response.status})`, status: response.status, transport: true };
      }
      const text = await response.text();
      try {
        return JSON.parse(text);
      } catch (e) {
        return { error_message: 'Нет сети (JSON parsing error)', transport: true };
      }
    } catch (e) {
      clearTimeout(timerId);
      if (e.name === 'AbortError') {
        if (isPageUnloading) return {};
        sendLogToFlutter(
          `Request Timeout\n` +
          `Dashboard URL: ${window.location.href}\n` +
          `Request: ${options.method || 'GET'} ${url}\n` +
          `Request Body: ${options.body || ''}`
        );
        return { error_message: 'Нет сети (Request timeout)', transport: true };
      }
      sendLogToFlutter(
        `Network Error\n` +
        `Dashboard URL: ${window.location.href}\n` +
        `Request: ${options.method || 'GET'} ${url}\n` +
        `Request Body: ${options.body || ''}\n` +
        `Error: ${e.message}\n${e.stack || ''}`
      );
      return { error_message: 'Нет сети (Network Error)', transport: true };
    }
  },

  fillUserData(userData, clientId) {
    const container = document.getElementById('user-cards');
    const params = getQueryParams();
    container.innerHTML = '';

    if (userData.alerts && Array.isArray(userData.alerts)) {
      // Получаем список уже показанных алертов из localStorage
      const alertsStorageKey = 'glaz_alerts_shown';
      let shownAlerts = [];
      
      try {
        const shownAlertsJson = localStorage.getItem(alertsStorageKey);
        if (shownAlertsJson) {
          const parsed = JSON.parse(shownAlertsJson);
          shownAlerts = Array.isArray(parsed) ? parsed : [];
        }
      } catch (e) {
        console.error('Error parsing shown alerts from localStorage:', e);
        shownAlerts = [];
      }
      
      for (const alert of userData.alerts) {
        // Пропускаем некорректные объекты
        if (!alert || typeof alert !== 'object') {
          continue;
        }
        
        // Проверяем наличие обязательных полей
        if (!alert.key || !alert.text) {
          console.warn('Alert missing required fields (key or text):', alert);
          continue;
        }
        
        // Проверяем, показывали ли уже этот алерт
        if (!shownAlerts.includes(alert.key)) {
          // Формируем callbackData согласно формату
          const callbackData = {
            key: String(alert.key),
            text: String(alert.text),
            ...(alert.title && { title: String(alert.title) }),
            buttons: Array.isArray(alert.buttons) ? alert.buttons.map(btn => ({
              title: btn?.title ? String(btn.title) : '',
              link: btn?.link ? String(btn.link) : ''
            })) : []
          };
          
          try {
            // Отправляем событие в Flutter
            flutterMessenger.send('alert', JSON.stringify(callbackData));
            
            // Сохраняем key в localStorage
            shownAlerts.push(alert.key);
            localStorage.setItem(alertsStorageKey, JSON.stringify(shownAlerts));
          } catch (e) {
            console.error('Error sending alert:', e);
          }
        }
      }
    }
    if (userData.dashboard_cards) {
      const blocks = buildBlocks(userData.dashboard_cards);
      for (const block of blocks) {
        if (block.type === 'single') {
          container.insertAdjacentHTML('beforeend', renderCard(block.card, params));
        } else {
          let html = '<div class="user-card-group">';
          for (const card of block.cards) {
            html += renderCard(card, params);
          }
          html += '</div>';
          container.insertAdjacentHTML('beforeend', html);
        }
      }
    } else {
      container.insertAdjacentHTML('beforeend', templates.key({ clientId }));
    }

    if (referralApplied) {
      const elapsed = Date.now() - referralAppliedAt;
      const remaining = 30000 - elapsed;
      if (remaining > 0) {
        container.insertAdjacentHTML('beforeend', renderReferralBanner());
        const sBanner = document.getElementById('referral-banner');
        document.getElementById('referral-banner-header').style.display = 'none';
        document.getElementById('referral-banner-body').style.display = 'none';
        document.getElementById('referral-success').style.display = 'flex';
        setTimeout(() => {
          sBanner.style.transition = 'opacity 0.5s ease, max-height 0.5s ease, margin 0.5s ease, border-width 0.5s ease';
          sBanner.style.opacity = '0';
          sBanner.style.maxHeight = '0';
          sBanner.style.marginBottom = '0';
          sBanner.style.borderWidth = '0';
          sBanner.style.overflow = 'hidden';
          setTimeout(() => { sBanner.remove(); referralApplied = false; }, 500);
        }, remaining);
      } else {
        referralApplied = false;
      }
    } else if (userData.show_referral_banner) {
      container.insertAdjacentHTML('beforeend', renderReferralBanner());
      this.initReferralBanner();
    }
  },

  showLoadingState() {
    const cards = document.querySelectorAll('.user-card[data-hide-when-reload="true"]');
    for (const card of cards) {
      const title = card.querySelector('.user-card-title');
      const content = card.querySelector('.user-card-content');
      if (title) title.textContent = '...';
      if (content) content.textContent = '...';
    }
  },

  async refreshUserData(jwt) {
    if (!jwt || typeof jwt !== 'string') return;
    refreshedJwt = jwt;
    this.showLoadingState();
    await this.loadUser();
  },

  initReferralBanner() {
    const banner = document.getElementById('referral-banner');
    const header = document.getElementById('referral-banner-header');
    const input = document.getElementById('referral-input');
    const submitBtn = document.getElementById('referral-submit');
    const errorEl = document.getElementById('referral-error');

    let isAnimating = false;
    header.addEventListener('click', () => {
      if (isAnimating) return;
      isAnimating = true;
      const prevCard = banner.previousElementSibling;
      if (!banner.classList.contains('expanded')) {
        // Разворачивание: сначала скрываем карточку, потом раскрываем баннер
        if (prevCard) prevCard.classList.add('referral-hidden');
        setTimeout(() => {
          banner.classList.add('expanded');
          setTimeout(() => {
            input.focus();
            isAnimating = false;
          }, 300);
        }, prevCard ? 300 : 0);
      } else {
        // Сворачивание: сначала сворачиваем баннер, потом показываем карточку
        banner.classList.remove('expanded');
        setTimeout(() => {
          if (prevCard) prevCard.classList.remove('referral-hidden');
          isAnimating = false;
        }, 300);
      }
    });

    input.addEventListener('input', () => {
      input.classList.remove('error');
      errorEl.style.display = 'none';
    });

    submitBtn.addEventListener('click', () => {
      this.submitReferralCode();
    });

    input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        this.submitReferralCode();
      }
    });
  },

  async submitReferralCode() {
    const banner = document.getElementById('referral-banner');
    const header = document.getElementById('referral-banner-header');
    const body = document.getElementById('referral-banner-body');
    const input = document.getElementById('referral-input');
    const submitBtn = document.getElementById('referral-submit');
    const submitText = submitBtn.querySelector('.referral-submit-text');
    const submitLoader = submitBtn.querySelector('.referral-submit-loader');
    const errorEl = document.getElementById('referral-error');
    const successEl = document.getElementById('referral-success');

    const code = input.value.trim();
    if (!code) {
      input.classList.add('error');
      errorEl.textContent = window.strings.referral_error;
      errorEl.style.display = 'block';
      return;
    }

    submitText.style.display = 'none';
    submitLoader.style.display = 'inline-block';
    submitBtn.disabled = true;
    input.disabled = true;

    const jwt = getCurrentJwt();
    const url = 'https://airlink-gate.ru/webhook/9aExHcMnHiLpCdxDi6AX67ay/auth/referral_apply';
    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${jwt}`,
        },
        body: JSON.stringify({ referral_code: code }),
      });

      if (response.ok) {
        banner.classList.remove('expanded');
        const prevCard = banner.previousElementSibling;
        if (prevCard) prevCard.classList.remove('referral-hidden');
        header.style.display = 'none';
        body.style.display = 'none';
        successEl.style.display = 'flex';

        referralApplied = true;
        referralAppliedAt = Date.now();

        // Через 3с обновляем дашборд — галочка сохранится через referralApplied флаг
        setTimeout(() => {
          const freshJwt = getCurrentJwt();
          if (freshJwt) window.onAppRefresh(freshJwt);
        }, 2000);
      } else {
        let errorMessage = window.strings.referral_error;
        let responseBody = '';
        try {
          responseBody = await response.text();
          const errData = JSON.parse(responseBody);
          if (errData.error_message) {
            errorMessage = errData.error_message;
          }
        } catch (_) {}

        if (response.status >= 404) {
          sendLogToFlutter(
            `HTTP Error ${response.status}\n` +
            `Dashboard URL: ${window.location.href}\n` +
            `Request: POST ${url}\n` +
            `Request Body: ${JSON.stringify({ referral_code: code })}\n` +
            `Response: ${responseBody}`
          );
        }

        input.classList.add('error');
        errorEl.textContent = errorMessage;
        errorEl.style.display = 'block';
        submitText.style.display = 'inline';
        submitLoader.style.display = 'none';
        submitBtn.disabled = false;
        input.disabled = false;
      }
    } catch (e) {
      sendLogToFlutter(
        `Network Error\n` +
        `Dashboard URL: ${window.location.href}\n` +
        `Request: POST ${url}\n` +
        `Request Body: ${JSON.stringify({ referral_code: code })}\n` +
        `Error: ${e.message}\n${e.stack || ''}`
      );

      input.classList.add('error');
      errorEl.textContent = window.strings.referral_error;
      errorEl.style.display = 'block';
      submitText.style.display = 'inline';
      submitLoader.style.display = 'none';
      submitBtn.disabled = false;
      input.disabled = false;
    }
  },
};

(function () {
  const start = () => app.run().catch((e) => {
    loaderOverlay.hide();
    flutterMessenger.send('fatal_error', e?.message || 'Unknown fatal error');
  });
  if (document.readyState === 'complete') {
    start();
  } else {
    window.addEventListener('load', start);
  }
})();
